import { type ReactElement, useEffect, useRef, useMemo } from 'react'
import { Landmark, Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { usePayments } from '@/features/payments/hooks/usePayments'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import { useInfiniteScrollTrigger } from '../hooks/useInfiniteScrollTrigger'
import { useStableOrder } from '../hooks/useStableOrder'
import { useViewportScrollAreaHeight } from '../hooks/useViewportScrollAreaHeight'
import { matchesAmount, normalizeAmountQuery } from '../utils/amountSearch'
import { PaymentCard } from './PaymentCard'
import { ColumnMonthNav } from './ColumnMonthNav'

const PAGE_SIZE = 50

interface PaymentsColumnProps {
  year: number
  monthIndex: number
  onMonthChange: (year: number, monthIndex: number) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  isDualColumn: boolean
  canDragPayments: boolean
  canDropInvoices: boolean
  isDragActive: boolean
  showSatisfactionStatus: boolean
  showInvoiceStatus: boolean
  onEditInvoice: (invoiceUuid: string) => Promise<void> | void
  onConnectBank: () => void
  recentlyLinkedPaymentId?: string | null
  className?: string
}

export function PaymentsColumn({
  year,
  monthIndex,
  onMonthChange,
  searchQuery,
  onSearchChange,
  isDualColumn,
  canDragPayments,
  canDropInvoices,
  isDragActive,
  showSatisfactionStatus,
  showInvoiceStatus,
  onEditInvoice,
  onConnectBank,
  recentlyLinkedPaymentId,
  className,
}: PaymentsColumnProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePayments(year, monthIndex, PAGE_SIZE)

  const payments = useMemo(
    () => data?.pages.flatMap(p => p.content) ?? [],
    [data],
  )
  const orderedPayments = useStableOrder(payments, payment => payment.id, `${year}-${monthIndex}`)

  const filteredPayments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (query.length === 0) return orderedPayments
    const amountQuery = normalizeAmountQuery(searchQuery)
    return orderedPayments.filter(payment => {
      const counterparty = (payment.counterpartyName ?? '').toLowerCase()
      const title = (payment.title ?? '').toLowerCase()
      if (counterparty.includes(query) || title.includes(query)) return true
      if (amountQuery && matchesAmount(payment.signedAmount, amountQuery)) return true
      return false
    })
  }, [orderedPayments, searchQuery])
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const scrollAreaHeight = useViewportScrollAreaHeight(scrollAreaRef)

  const { data: integrations } = useIntegrationsStatus()
  const hasBankConnection = useMemo(
    () => integrations?.some(i => i.provider === 'BANK' && i.status === 'ACTIVE') ?? false,
    [integrations],
  )

  useInfiniteScrollTrigger({
    scrollRef: scrollAreaRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    recomputeKey: `${scrollAreaHeight ?? 0}:${filteredPayments.length}`,
  })

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.key !== 'Escape') return
      if (searchQuery.length === 0) return
      onSearchChange('')
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [searchQuery, onSearchChange])

  return (
    <div className={cn('min-w-0', className)}>
      <ColumnMonthNav
        label={t('page.tabPayments')}
        year={year}
        monthIndex={monthIndex}
        onChange={onMonthChange}
      />

      <div className="mt-5 flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input
            type="text"
            value={searchQuery}
            onChange={event => onSearchChange(event.target.value)}
            placeholder={t('table.search.placeholder', {
              defaultValue: 'Search counterparty, title, or amount...',
            })}
            className="h-11 w-full rounded-lg border-border/70 bg-card pl-9 pr-9 focus-visible:ring-0 focus-visible:border-foreground/35"
          />
          {searchQuery.length > 0 ? (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label={t('table.search.clearAria', { defaultValue: 'Clear search' })}
              className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted/20 hover:text-muted-foreground"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      <div
        ref={scrollAreaRef}
        data-kanban-scroll-area="true"
        className="mt-3 space-y-3 overflow-y-auto overscroll-contain pr-1 scrollbar-subtle"
        style={scrollAreaHeight ? { height: `${scrollAreaHeight}px` } : undefined}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="size-5" />
          </div>
        ) : !hasBankConnection && orderedPayments.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <Landmark className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No bank connected</p>
            <Button variant="outline" size="sm" onClick={onConnectBank}>
              Connect bank
            </Button>
          </div>
        ) : orderedPayments.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No payments this month
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            {t('list.noSearchResults', { defaultValue: 'No matching payments found.' })}
          </div>
        ) : (
          filteredPayments.map((payment, i) => (
            <PaymentCard
              key={payment.id}
              index={i + 1}
              payment={payment}
              isDualColumn={isDualColumn}
              canDragPayments={canDragPayments}
              canDropInvoices={canDropInvoices}
              isDragActive={isDragActive}
              showSatisfactionStatus={showSatisfactionStatus}
              showInvoiceStatus={showInvoiceStatus}
              recentlyLinked={recentlyLinkedPaymentId === payment.id}
              onEditInvoice={onEditInvoice}
            />
          ))
        )}

        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <Spinner className="size-4" />
          </div>
        )}
      </div>
    </div>
  )
}
